import { defineStore } from 'pinia';
import { useSessionStore } from '@/store/session';
import { useAppStore } from '@/store/app';
import { isBookedServiceStatus, isSettledServiceStatus } from '@/utilities/serviceStatus';

export const usePackingMaterialsStore = defineStore('packing_materials', () => {
    const sessionStore = useSessionStore();
    const appStore = useAppStore();

    const exists = computed(() => !!getItem.value);

    const getItem = computed(() => {
        return sessionStore?.order?.items?.find((item) => item.productable_type === 'packing_materials') ?? null;
    });

    const getProduct = computed(() => {
        return getItem.value?.product ?? null;
    });

    const isCallbackRequested = computed(() => sessionStore.order?.packing_materials_callback_requested);

    const canRemove = computed(() => getItem.value?.status === 'UNCONFIRMED');

    const isConfirmed = computed(() => getItem.value?.status === 'CONFIRMED');

    const canIndividuallyBook = computed(() => {
        return getProduct.value?.status === 'QUOTED';
    });

    const getPaidButtonCopy = computed(() => {
        if (!isConfirmed.value) {
            return null;
        }

        const status = getProduct.value?.status;

        if (isSettledServiceStatus(status)) {
            return 'Paid';
        }

        // A booked service is confirmed and scheduled but not necessarily paid, so it can't claim
        // "Paid" — and it is emphatically not awaiting anyone's approval.
        if (isBookedServiceStatus(status)) {
            return 'Booked';
        }

        return 'Pending approval';
    });

    const hasFailedConfirmationPayment = computed(() => {
        if (getProduct.value?.confirmation_payment?.status == 'FAILED') {
            return true;
        }
        return false;
    });

    const isDisabled = computed(() => !appStore.packing_materials_enabled);

    async function remove() {
        await window.axios.delete(`/customers/book/orders/${sessionStore.getOrderId}/packing-materials`);
        await sessionStore.retrieve();
    }

    return {
        exists,
        getItem,
        getProduct,
        isCallbackRequested,
        canRemove,
        isConfirmed,
        canIndividuallyBook,
        getPaidButtonCopy,
        hasFailedConfirmationPayment,
        isDisabled,
        remove,
    };
});
